import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Minimize2, Maximize2 } from "lucide-react";
import PencilIcon from "@/assets/images/mdi-pencil.svg";
import { useEffect, useState } from "react";
import { cn } from "@/libs/utils";
import EstimateIconRed from "@/assets/images/Tab/bid-estimate-icon-red.png";
import CommonImg from "@/components/Common/Img";
import ScenarioEstimateSelect from "./ScenarioEstimateSelect";
import { useEstimateInfo, useSetEstimateInfo } from "../hooks/useScenarioStore";
import { ProjectType } from "@/consts/Project";
import ScenarioEstimateAccordion from "./ScenarioEstimateAccordion";
import CreateOrEditScenarioContents from "./CreateOrEditScenarioContents";
import ScenarioApprovalHistoryButton from "./ScenarioApprovalHistoryButton";
import { useReadProjectGroupsScenarios } from "@/libs/react-query/hooks/useScenarioQuery";
import { useParams } from "react-router-dom";

export type ScenarioSubmitPayload = {
  title: string;
  latestSheetId: string;
  divisions: any[]; // Replace with BuyerGroupingTable[]
};

type ScenarioDialogDefaults = {
  sheetId: string;
  title: string;
  projectId: string;
  scenarioData: any[];
};

type Props = {
  mode: "create" | "edit";
  open: boolean;
  defaults?: ScenarioDialogDefaults;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onSubmit: (data: ScenarioSubmitPayload) => void;
};

export default function CreateOrEditScenarioDialog({
  mode,
  open,
  defaults,
  setOpen,
  onSubmit,
}: Props) {
  const [tab, setTab] = useState("selectEstimate");
  const [selectedAccordionValue, setSelectedAccordionValue] =
    useState<string>("");
  const [isMinimized, setIsMinimized] = useState(false);

  const estimateInfo = useEstimateInfo();
  const setEstimateInfo = useSetEstimateInfo();
  const { group_id = "" } = useParams();
  const { data } = useReadProjectGroupsScenarios(group_id);
  const { scenarios = [] } = data || {};

  const hasApprovedScenario = scenarios.some(
    (scenario) => !!scenario.approval_id
  );

  // 다이얼로그가 열릴 때 estimateInfo 초기화 (create 모드)
  useEffect(() => {
    if (open && mode === "create") {
      setEstimateInfo({
        projectId: "",
        projectType: ProjectType.PROJ,
        originalId: "",
        itemSheetId: "",
      });
      setSelectedAccordionValue("");
      setTab("selectEstimate");
    }
  }, [open, mode, setEstimateInfo]);

  useEffect(() => {
    if (mode === "edit" && defaults) {
      initializeEditMode();
    }
  }, [mode, defaults]);

  const initializeEditMode = () => {
    if (!defaults) return;

    setTab("createScenario");
    setSelectedAccordionValue(defaults.sheetId);
  };

  return (
    <div className="flex items-center gap-[10px]">
      {mode === "create" && (
        <ScenarioApprovalHistoryButton disabled={!hasApprovedScenario} />
      )}
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogTrigger>
          <>
            {mode === "create" ? (
              <div className="px-3 py-[6px] text-white flex items-center gap-2 rounded-sm text-sm font-semibold bg-dark-brown hover:bg-dark-brown/80">
                <Plus size={16} />
                <span>시나리오 추가</span>
              </div>
            ) : (
              <div className="text-green  px-5 py-2 flex gap-1 hover:bg-gray-E">
                <CommonImg src={PencilIcon} alt="U" className="w-3 h-3" />
                시나리오 수정
              </div>
            )}
          </>
        </AlertDialogTrigger>
        <AlertDialogContent
          className={cn(
            "max-w-[1000px] overflow-y-auto transition-all duration-200",
            // 최소화 상태일 때 오른쪽 하단에 작은 바로 표시
            isMinimized
              ? "!fixed !left-auto !right-4 !bottom-4 !top-auto !translate-x-0 !translate-y-0 !max-h-[60px] !w-[300px] !p-3"
              : "max-h-[80vh] !left-[60%]" // 기본 위치를 오른쪽으로 이동
          )}
        >
          {isMinimized ? (
            // 최소화된 상태 표시
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-gray-700">
                {mode === "create" ? "시나리오 생성" : `${defaults?.title} 수정`}
              </span>
              <button
                onClick={() => setIsMinimized(false)}
                className="p-1 rounded hover:bg-gray-100"
                title="복원"
              >
                <Maximize2 size={18} />
              </button>
            </div>
          ) : (
          <Tabs
            value={tab}
            onValueChange={setTab}
            className="w-full h-full overflow-y-auto"
          >
            <AlertDialogHeader
              extraAction={
                <button
                  onClick={() => setIsMinimized(true)}
                  className="p-1 rounded hover:bg-gray-100"
                  title="최소화"
                >
                  <Minimize2 size={18} />
                </button>
              }
            >
              <AlertDialogTitle>
                {mode === "create"
                  ? "시나리오 생성"
                  : `${defaults?.title} 수정`}
              </AlertDialogTitle>
              <AlertDialogDescription hidden />
            </AlertDialogHeader>

            <TabsList className="flex justify-center items-center mt-3 mb-6">
              <TabsTrigger value="selectEstimate" asChild>
                <Button
                  size="trigger"
                  className="bg-gray-D text-gray-B hover:bg-gray-D/90 data-[state=active]:text-white data-[state=active]:bg-dark-red"
                >
                  적용 산출내역서 선택
                </Button>
              </TabsTrigger>
              <TabsTrigger value="createScenario" asChild>
                <Button
                  size="trigger"
                  disabled={!selectedAccordionValue}
                  className="bg-gray-D text-gray-B hover:bg-gray-D/90 data-[state=active]:text-white data-[state=active]:bg-dark-red"
                >
                  시나리오 작성
                </Button>
              </TabsTrigger>
            </TabsList>

            <TabsContent value="selectEstimate">
              <div className="w-full bg-white rounded-xl border-[1px] border-gray-D">
                <div className="flex justify-between items-center px-5 py-2 pb-2">
                  <div className="relative flex gap-2 items-center">
                    <CommonImg src={EstimateIconRed} alt={"estimate"} />
                    <span className="text-sm text-gray-A font-semibold">
                      적용 산출내역서 선택
                    </span>
                  </div>
                  <ScenarioEstimateSelect />
                </div>
                <div className="min-h-[280px] border-gray-D border-t-[1px]">
                  {estimateInfo?.projectId ? (
                    <ScenarioEstimateAccordion
                      selectedAccordionValue={selectedAccordionValue}
                      setSelectedAccordionValue={setSelectedAccordionValue}
                    />
                  ) : (
                    <EmptyComponent />
                  )}
                </div>
              </div>
              <div className="flex justify-center mt-6">
                <Button
                  className="w-[150px] h-10 bg-dark-brown hover:bg-dark-brown/80 text-white"
                  onClick={() => setTab("createScenario")}
                  disabled={!selectedAccordionValue}
                >
                  시나리오 선택
                </Button>
              </div>
            </TabsContent>

            <TabsContent value="createScenario">
              <CreateOrEditScenarioContents
                latestSheetId={selectedAccordionValue}
                setOpen={setOpen}
                defaultTitle={defaults?.title}
                existingScenarioData={defaults?.scenarioData || []}
                onSubmit={onSubmit}
                mode={mode}
                defaultProjectId={defaults?.projectId}
              />
            </TabsContent>
          </Tabs>
          )}
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

const EmptyComponent = () => (
  <div className="h-20 flex items-center justify-center">
    <span className="text-gray-B text-sm font-semibold">
      공고를 선택하세요.
    </span>
  </div>
);
